// Shared matching predicate between AI detections and inspector ground truth.
// Single source of truth for "does this AI detection correspond to a ground-truth
// detection?" — used by the Comparison tab's covered/proposed sets and the
// accuracy-metrics panel.

export interface MatchMarker {
  mainCode: string;
  meters: number;
}

export interface MatchGroundTruth {
  main_Code: string;
  distance: number;
}

// True when a detection of `mainCode` at `distance` has a ground-truth detection
// of the same main code within `tolerance` metres.
export const matchesWithinTolerance = (
  mainCode: string,
  distance: number,
  groundTruth: MatchGroundTruth[],
  tolerance: number
): boolean =>
  groundTruth.some(
    (g) => g.main_Code === mainCode && Math.abs(g.distance - distance) <= tolerance
  );

// Per-code tallies. tp/fp/fn count AI detections (tp/fp) and unmatched inspector
// rows (fn); `gt` counts the inspector's own rows for that code, which is what the
// inspector-side view needs (gt - fn = inspector rows the AI confirmed). gt is not
// tp + fn: several AI detections can match the same inspector row.
export interface CodeTally {
  tp: number;
  fp: number;
  fn: number;
  gt: number;
}

export interface ComparisonClassification<M, G> {
  matched: M[]; // AI detections that match a ground-truth row (true positives)
  aiOnly: M[]; // AI detections with no ground-truth match (false positives)
  inspectorOnly: G[]; // ground-truth rows the AI missed (false negatives)
  byCode: Map<string, CodeTally>;
}

// Classify AI markers against the inspector ground truth at a given tolerance.
export function classifyComparison<
  M extends MatchMarker,
  G extends MatchGroundTruth
>(markers: M[], groundTruth: G[], tolerance: number): ComparisonClassification<M, G> {
  const matched: M[] = [];
  const aiOnly: M[] = [];
  markers.forEach((m) => {
    if (matchesWithinTolerance(m.mainCode, m.meters, groundTruth, tolerance)) matched.push(m);
    else aiOnly.push(m);
  });

  const inspectorOnly = groundTruth.filter(
    (g) =>
      !markers.some(
        (m) => m.mainCode === g.main_Code && Math.abs(m.meters - g.distance) <= tolerance
      )
  );

  const byCode = new Map<string, CodeTally>();
  const bucket = (code: string) => {
    let b = byCode.get(code);
    if (!b) {
      b = { tp: 0, fp: 0, fn: 0, gt: 0 };
      byCode.set(code, b);
    }
    return b;
  };
  matched.forEach((m) => bucket(m.mainCode).tp++);
  aiOnly.forEach((m) => bucket(m.mainCode).fp++);
  inspectorOnly.forEach((g) => bucket(g.main_Code).fn++);
  groundTruth.forEach((g) => bucket(g.main_Code).gt++);

  return { matched, aiOnly, inspectorOnly, byCode };
}

// Precision = matched AI / all AI detections (null when there are no AI detections).
export const precisionOf = (c: ComparisonClassification<any, any>): number | null => {
  const total = c.matched.length + c.aiOnly.length;
  return total > 0 ? c.matched.length / total : null;
};

// Recall = matched ground-truth / all ground-truth rows (null when no ground truth).
export const recallOf = (
  c: ComparisonClassification<any, any>,
  groundTruthCount: number
): number | null => {
  if (groundTruthCount <= 0) return null;
  const gtMatched = groundTruthCount - c.inspectorOnly.length;
  return gtMatched / groundTruthCount;
};
